import { type NavigateFunction } from "react-router-dom";
import { type AppDispatch } from "../../store/store";

const NORMAL_RECONNECT_DELAYS_MS = [3000, 5000, 10000, 20000, 30000] as const
const AUTH_RECONNECT_DELAYS_MS = [30000, 60000, 120000] as const
const CONNECT_THROTTLE_MS = 1200
const JITTER_FACTOR = 0.25

type SocketCallback = (webSocket: WebSocket, dispatch: AppDispatch, navigate: NavigateFunction) => void

interface SocketManagerState {
    activeWebSocket: WebSocket | null
    reconnectTimer: ReturnType<typeof setTimeout> | null
    reconnectAttempts: number
    authReconnectAttempts: number
    lastConnectTimestamp: number
    lastActivityTimestamp: number
    disposed: boolean
    waitForOnline: boolean
    lifecycleId: string
    connectInitiator: string | null
    currentContext: {
        dispatch: AppDispatch
        navigate: NavigateFunction
        callbackSocket: SocketCallback
    } | null
    messageActivityListener?: (event: MessageEvent) => void
    onlineListener?: () => void
    offlineListener?: () => void
}

const managerState = (() => {
    const key = "__BSO_WS_MANAGER_STATE__"
    const globalObject = globalThis as Record<string, unknown>
    if (!globalObject[key]) {
        globalObject[key] = {
            activeWebSocket: null,
            reconnectTimer: null,
            reconnectAttempts: 0,
            authReconnectAttempts: 0,
            lastConnectTimestamp: 0,
            lastActivityTimestamp: Date.now(),
            disposed: false,
            waitForOnline: false,
            lifecycleId: "initial",
            connectInitiator: null,
            currentContext: null,
            onlineListener: undefined,
            offlineListener: undefined
        } satisfies SocketManagerState
    }
    return globalObject[key] as SocketManagerState
})()

const createLifecycleId = (): string => {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

const setLastActivityNow = (): void => {
    managerState.lastActivityTimestamp = Date.now()
}

const extractHttpStatusCode = (reason: string): number | null => {
    const match = reason.match(/\b(4\d{2,3}|401|403)\b/)
    if (!match?.[1]) {
        return null
    }
    const statusCode = Number(match[1])
    return Number.isFinite(statusCode) ? statusCode : null
}

const isAuthLikeClose = (closeEvent: CloseEvent | null): boolean => {
    const reason = (closeEvent?.reason ?? "").toLowerCase()
    const statusCode = extractHttpStatusCode(reason)
    const is401or403 = statusCode === 401 || statusCode === 403
    const is4xxHttp = statusCode !== null && statusCode >= 400 && statusCode < 500
    const is4xxxWs = closeEvent?.code != null && closeEvent.code >= 4000 && closeEvent.code < 5000
    const hasAuthHint = /auth|unauthor|forbid|token|handshake|policy/.test(reason)
    const isPolicyViolation = closeEvent?.code === 1008
    return Boolean(is401or403 || is4xxHttp || is4xxxWs || hasAuthHint || isPolicyViolation)
}

const withJitter = (baseDelay: number): number => {
    const jitterRange = Math.floor(baseDelay * JITTER_FACTOR)
    const jitter = Math.floor(Math.random() * (jitterRange + 1))
    return baseDelay + jitter
}

const getDelayFromPlan = (plan: readonly number[], attempt: number): number => {
    const index = Math.max(0, Math.min(plan.length - 1, attempt - 1))
    return plan[index]
}

const clearReconnectTimer = (): void => {
    if (managerState.reconnectTimer !== null) {
        clearTimeout(managerState.reconnectTimer)
        managerState.reconnectTimer = null
    }
}

const closeSocketAndCleanup = (socket: WebSocket | null, reason: string): void => {
    if (socket == null) {
        return
    }

    socket.onopen = null
    socket.onclose = null
    socket.onerror = null
    socket.onmessage = null

    if (managerState.messageActivityListener != null) {
        socket.removeEventListener("message", managerState.messageActivityListener)
        managerState.messageActivityListener = undefined
    }

    if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
        try {
            socket.close(1000, reason)
        } catch (error) {
            console.warn("[WS] close failed during cleanup", error)
        }
    }
}

const logDiagnostics = (message: string, payload: Record<string, unknown> = {}): void => {
    const socket = managerState.activeWebSocket
    const readyState = socket?.readyState ?? null
    const listenerCount = managerState.messageActivityListener ? 1 : 0
    console.info(`[WS][${managerState.lifecycleId}] ${message}`, {
        readyState,
        reconnectAttempts: managerState.reconnectAttempts,
        authReconnectAttempts: managerState.authReconnectAttempts,
        waitForOnline: managerState.waitForOnline,
        activeSocket: Boolean(socket),
        listeners: listenerCount,
        connectInitiator: managerState.connectInitiator,
        ...payload
    })
}

const getVisibilityMultiplier = (): number => {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return 2
    }
    return 1
}

const isBrowserOnline = (): boolean => {
    if (typeof navigator === "undefined") {
        return true
    }
    return navigator.onLine
}

const scheduleReconnect = (closeEvent: CloseEvent | null, reason: string): void => {
    if (managerState.disposed || managerState.currentContext == null) {
        return
    }

    if (managerState.reconnectTimer !== null) {
        return
    }

    if (!isBrowserOnline()) {
        managerState.waitForOnline = true
        logDiagnostics("Reconnect paused: offline", { reason })
        return
    }

    const isAuthLike = isAuthLikeClose(closeEvent)
    if (isAuthLike) {
        managerState.authReconnectAttempts += 1
    } else {
        managerState.reconnectAttempts += 1
    }

    const attempt = isAuthLike ? managerState.authReconnectAttempts : managerState.reconnectAttempts
    const baseDelay = isAuthLike
        ? getDelayFromPlan(AUTH_RECONNECT_DELAYS_MS, attempt)
        : getDelayFromPlan(NORMAL_RECONNECT_DELAYS_MS, attempt)
    const visibilityMultiplier = getVisibilityMultiplier()
    const delayWithVisibility = Math.floor(baseDelay * visibilityMultiplier)
    const selectedDelay = withJitter(delayWithVisibility)

    logDiagnostics("Scheduling reconnect", {
        reason,
        attempt,
        isAuthLike,
        selectedDelay,
        baseDelay,
        closeCode: closeEvent?.code ?? null,
        closeReason: closeEvent?.reason ?? null
    })

    managerState.reconnectTimer = setTimeout(() => {
        managerState.reconnectTimer = null
        if (managerState.disposed || managerState.currentContext == null) {
            return
        }
        initializeWebSocket(null, managerState.currentContext.dispatch, managerState.currentContext.navigate, managerState.currentContext.callbackSocket, {
            caller: "reconnect-timer"
        })
    }, selectedDelay)
}

const onOnline = (): void => {
    if (managerState.disposed || !managerState.waitForOnline || managerState.currentContext == null) {
        return
    }
    managerState.waitForOnline = false
    logDiagnostics("Online event: reconnecting")
    initializeWebSocket(null, managerState.currentContext.dispatch, managerState.currentContext.navigate, managerState.currentContext.callbackSocket, {
        caller: "online-event"
    })
}

const onOffline = (): void => {
    managerState.waitForOnline = true
    clearReconnectTimer()
    logDiagnostics("Offline event: reconnect paused")
}

if (typeof window !== "undefined") {
    if (managerState.onlineListener) {
        window.removeEventListener("online", managerState.onlineListener)
    }
    if (managerState.offlineListener) {
        window.removeEventListener("offline", managerState.offlineListener)
    }
    managerState.onlineListener = onOnline
    managerState.offlineListener = onOffline
    window.addEventListener("online", managerState.onlineListener)
    window.addEventListener("offline", managerState.offlineListener)
}

export const getWebSocketDiagnostics = (): {
    hasActiveSocket: boolean
    readyState: number | null
    reconnectAttempts: number
    authReconnectAttempts: number
    lastActivityTimestamp: number
    lifecycleId: string
    hasReconnectTimer: boolean
    waitForOnline: boolean
    disposed: boolean
} => {
    return {
        hasActiveSocket: managerState.activeWebSocket != null,
        readyState: managerState.activeWebSocket?.readyState ?? null,
        reconnectAttempts: managerState.reconnectAttempts,
        authReconnectAttempts: managerState.authReconnectAttempts,
        lastActivityTimestamp: managerState.lastActivityTimestamp,
        lifecycleId: managerState.lifecycleId,
        hasReconnectTimer: managerState.reconnectTimer != null,
        waitForOnline: managerState.waitForOnline,
        disposed: managerState.disposed
    }
}

export const disposeWebSocket = (reason = "dispose"): void => {
    managerState.disposed = true
    managerState.waitForOnline = false
    clearReconnectTimer()
    closeSocketAndCleanup(managerState.activeWebSocket, reason)
    managerState.activeWebSocket = null
    managerState.currentContext = null
    logDiagnostics("Disposed socket manager", { reason })
}

export const initializeWebSocket = (
    _webSocket: null | WebSocket,
    dispatch: AppDispatch,
    navigate: NavigateFunction,
    callbackSocket: SocketCallback,
    options?: { forceReconnect?: boolean, caller?: string, reason?: string }
): void => {
    const isDevMode = process.env.REACT_APP_IS_DEV === 'true'
    const shouldForceReconnect = options?.forceReconnect === true
    const caller = options?.caller ?? "unknown"
    const reason = options?.reason ?? (shouldForceReconnect ? "force-reconnect" : "connect")

    managerState.disposed = false
    managerState.currentContext = { dispatch, navigate, callbackSocket }
    managerState.connectInitiator = caller

    const state = managerState.activeWebSocket?.readyState
    if ((state === WebSocket.OPEN || state === WebSocket.CONNECTING) && !shouldForceReconnect) {
        logDiagnostics("Connect ignored: socket already active", { caller, reason })
        return
    }

    if (state === WebSocket.CLOSING && !shouldForceReconnect) {
        logDiagnostics("Connect ignored: socket is closing", { caller, reason })
        return
    }

    if (shouldForceReconnect) {
        closeSocketAndCleanup(managerState.activeWebSocket, "force-reconnect")
        managerState.activeWebSocket = null
        clearReconnectTimer()
        logDiagnostics("Force reconnect requested", { caller, reason })
    }

    if (managerState.reconnectTimer !== null && !shouldForceReconnect) {
        logDiagnostics("Connect ignored: reconnect timer already scheduled", { caller, reason })
        return
    }

    if (!isBrowserOnline()) {
        managerState.waitForOnline = true
        logDiagnostics("Connect paused: offline", { caller, reason })
        return
    }

    const now = Date.now()
    const elapsed = now - managerState.lastConnectTimestamp
    if (elapsed < CONNECT_THROTTLE_MS) {
        clearReconnectTimer()
        managerState.reconnectTimer = setTimeout(() => {
            managerState.reconnectTimer = null
            initializeWebSocket(null, dispatch, navigate, callbackSocket, {
                caller: "connect-throttle",
                reason
            })
        }, CONNECT_THROTTLE_MS - elapsed)
        logDiagnostics("Connect throttled", {
            caller,
            reason,
            throttleDelay: CONNECT_THROTTLE_MS - elapsed
        })
        return
    }

    managerState.lastConnectTimestamp = now
    managerState.lifecycleId = createLifecycleId()

    const socket = new WebSocket(isDevMode ? 'ws://localhost:42424/ws' : 'wss://api.bso.ae/ws')
    managerState.activeWebSocket = socket
    setLastActivityNow()

    managerState.messageActivityListener = () => {
        setLastActivityNow()
    }
    socket.addEventListener("message", managerState.messageActivityListener)

    callbackSocket(socket, dispatch, navigate)

    socket.onopen = () => {
        managerState.waitForOnline = false
        managerState.reconnectAttempts = 0
        managerState.authReconnectAttempts = 0
        setLastActivityNow()
        logDiagnostics("Socket connected", { caller, reason })
    }

    socket.onerror = (error) => {
        logDiagnostics("Socket error", {
            caller,
            reason,
            error: String((error as Event)?.type ?? "unknown-error")
        })
        if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
            socket.close(1011, "socket-error")
        }
    }

    socket.onclose = (event) => {
        setLastActivityNow()
        if (managerState.activeWebSocket === socket) {
            managerState.activeWebSocket = null
        }

        closeSocketAndCleanup(socket, "closed")

        logDiagnostics("Socket closed", {
            caller,
            reason,
            closeCode: event.code,
            closeReason: event.reason || null,
            wasClean: event.wasClean
        })

        if (managerState.disposed) {
            return
        }

        if (event.code === 1000) {
            managerState.reconnectAttempts = 0
            managerState.authReconnectAttempts = 0
            return
        }

        scheduleReconnect(event, reason)
    }
}
